<template>
  <div class="tw:flex tw:items-end tw:justify-between">
    <div class="tw:flex tw:flex-col tw:w-full tw:mr-2">
      <p class="tw:font-bold tw:mb-1">{{ $t('check_out_voucher_field') }}</p>
      <BaseTextField
        name="rcVoucher"
        aria-label="rcVoucher"
        size="medium"
        type="text"
        class="tw:w-full tw:mr-2"
        dataTestId="rc-checkout-voucher-input"
        :disabled="!!voucherData?.voucherCode"
        :class="getClass()"
        :value="code"
        :successValidation="voucherData?.isValid"
        :withLoader="false"
        @onChange="setVoucherCode"
        @keydown.enter="applyVoucherCode('apply')"
      />
    </div>
    <GhostButton
      size="medium"
      class="tw:whitespace-nowrap"
      dataTestId="rc-checkout-voucher-apply-button"
      @click="applyVoucherCode(!voucherData ? 'apply' : 'remove')"
      :disabled="isLoading"
    >
      {{ !voucherData ? $t('voucher_apply') : $t('voucher_remove') }}
    </GhostButton>
  </div>
  <div v-if="voucherData?.isValid" class="tw:mt-2 tw:text-green-700 tw:flex tw:items-center">
    <TagIcon class="tw:mr-1 tw:fill-green-700 tw:size-[18px]" />
    <p data-testid="rc-checkout-voucher-success-message">
      {{ $t('rc_checkout_discount_applied', { DiscountAmount: formatCurrency(voucherData.creditAmount) }) }}
    </p>
  </div>
  <p class="tw:text-red-700" v-if="!voucherData?.isValid && voucherData?.invalidReason === 'VOUCHER_NOT_ACTIVE'">
    {{ $t('voucher_error_expired') }}
  </p>
  <p
    data-testid="rc-checkout-voucher-error-message"
    class="tw:text-red-700"
    v-if="!voucherData?.isValid && ['VOUCHER_NOT_FOUND', 'WRONG_CURRENCY'].includes(voucherData?.invalidReason)"
  >
    {{ $t('voucher_error') }}
  </p>
</template>

<script setup>
import { ref } from 'vue'
import appAnalytics from '@/utils/tracking'

import GhostButton from '@/components/base/buttons/GhostButton.vue'
import BaseTextField from '@/components/base/inputs/BaseTextField.vue'
import TagIcon from '@/components/icons/TagIcon.svg?component'

import { selectedAccommodationId } from '@/layouts/queries'
import { useFormatting } from '@/composables/useFormatting'

const code = ref('')
const { formatCurrency } = useFormatting()

const props = defineProps({
  voucherData: Object,
  isLoading: Boolean,
  initialCode: {
    type: String,
    default: '',
  },
})

if (props.initialCode) {
  code.value = props.initialCode
}

const emit = defineEmits(['applyVoucherCode'])

const applyVoucherCode = type => {
  if (type === 'apply') {
    appAnalytics.track(appAnalytics.events.RC_APPLY_VOUCHER, {
      item_id: selectedAccommodationId.value,
      promoCode: code.value,
    })
    emit('applyVoucherCode', code.value)
  }
  if (type === 'remove') {
    appAnalytics.track(appAnalytics.events.RC_REMOVE_VOUCHER, {
      item_id: selectedAccommodationId.value,
    })
    emit('applyVoucherCode', '')
    code.value = ''
  }
}

const setVoucherCode = e => {
  code.value = e
}

const getClass = () => {
  const classes = []
  if (props.voucherData && props.voucherData !== null) {
    if (props.voucherData?.isValid) {
      classes.push('tw:border-green-700 tw:text-green-700')
    } else if (!props.voucherData?.isValid && code.value) {
      classes.push('tw:border-red-700')
    }
  }
  return classes.join(' ')
}
</script>
